/**
 * SYM-6213. How many template previews the editor may have in flight, and what it does when the
 * server says it is too busy to draw one.
 *
 * Production went down twice on 2026-09-15. One browser sent 112 preview requests in 23 seconds,
 * another sent 139 in about 40, and every one of them started an image conversion on the API box
 * at the same moment. The kernel ran out of memory and killed Redis eleven times. Nothing in the
 * editor limited how many it asked for at once, and nothing in it knew how to wait.
 *
 * Lives here rather than inside AdEditorContext for the same reason the other preview helpers do:
 * this app has no component tests, so a rule kept inside the provider cannot be exercised, and a
 * queue is exactly the kind of thing that is easy to get subtly wrong.
 */

/**
 * At most this many preview requests leave the browser at the same time, tiles and rail together.
 *
 * ⚠️ TWO, to match the server's two render slots (PREVIEW_RENDER_SLOTS). It was three, and the
 * third request was refused almost every time: on QA, 16 Sep 2026, 11 of 20 panel requests came
 * back 503, each costing a wait of at least 2 seconds, and the panel took 10-15 seconds to fill.
 */
export const MAX_PREVIEWS_IN_FLIGHT = 2;

/**
 * A "busy" answer is retried this many times before the tile gives up and says so. Three waits of
 * 1, 2 and 4 seconds cover a rush without leaving a spinner running for a minute.
 */
export const MAX_BUSY_RETRIES = 3;

const FALLBACK_RETRY_DELAYS_MS = [1000, 2000, 4000];

/**
 * The longest wait a tile sits through before retrying. A server asking for LONGER is not retried
 * early: the tile stops and says it is busy.
 *
 * ⚠️ It used to retry after this cap even when the server asked for more, which only earned another
 * refusal inside the same limit window (Astra's second opinion, 16 Sep 2026). A per-minute limit can
 * legitimately ask for most of a minute, and a tile spinning that long reads as a hang -- the state
 * SYM-5919 records two people reporting as one. So: stop the spinner, respect the wait.
 */
const MAX_RETRY_DELAY_MS = 10000;

/**
 * SYM-6247. The longest wait the editor sits out BY ITSELF when the server asks for more than a
 * tile should spin: the per-user limit is per minute, so its Retry-After can be most of a minute.
 *
 * Giving up at once was the behaviour until the day this went live (17 Sep 2026). Mark edited texts
 * at a normal pace, crossed the per-user limit once, and all six tiles and the main ad stopped
 * together on "Busy right now -- open the panel again in a moment.", which nobody could act on:
 * reopening the panel asks for nothing, because a tile with a message counts as answered.
 *
 * The two earlier lessons stay: the tile does NOT spin through it (SYM-5919, a long spinner reads as
 * a hang) -- the caller is told through `onWaiting` and shows a calm notice -- and it does NOT retry
 * early (Astra's second opinion, 16 Sep 2026): the full wait is respected, then one retry goes out.
 * A server asking for longer than this is still not waited for.
 */
const MAX_BUSY_WAIT_MS = 65000;

export type PreviewJob = {
  /** Identifies the render. A key already queued or in flight is not queued a second time. */
  key: string;
  /** Lets a caller drop every queued job belonging to one ad at once. */
  group: string;
  /** Lower runs first. Ties run in the order they were queued. */
  priority: number;
  run: () => Promise<unknown>;
  /** Called once the job's slot has been released, so a follow-up may reuse its key. */
  onSettled?: () => void;
  /**
   * SYM-6247. Asked when the job reaches the front of the queue. A job that says no is discarded
   * unrun: while the queue was held (see holdFor) the customer may have edited again, and the
   * superseded request would only spend one of the per-user budget on a picture nobody shows.
   */
  stillWanted?: () => boolean;
  /**
   * SYM-6247. Told once, with the time left, when the job is found waiting behind a cooldown (see
   * holdFor). A job queued BEFORE the refusal arrived would otherwise sit there as "rendering" for
   * up to a minute: seen on QA, 18 Sep 2026, four tiles saying Rendering next to four saying Busy.
   */
  onHeld?: (heldMs: number) => void;
};

export type PreviewQueue = {
  /** False when the same key is already queued or in flight, so nothing was added. */
  enqueue: (job: PreviewJob) => boolean;
  /** Removes queued jobs that have not started. A job already in flight is left to finish. */
  drop: (shouldDrop: (job: PreviewJob) => boolean) => PreviewJob[];
  inFlight: () => number;
  queued: () => number;
  /**
   * SYM-6247 (Astra's eighth review). Start nothing before this moment. The per-user limit refuses
   * EVERY request from this user until its window resets, so one refusal's Retry-After is a
   * cooldown for the whole editor, not for the one picture that happened to be refused: a retry,
   * a tile healing itself, a fresh edit or a newly opened panel must all wait for it. Before this
   * existed, a tile that ended on a 300 s refusal healed after 30 s and asked again 270 s early.
   * Only ever extends; a shorter cooldown than the one already running is ignored.
   */
  holdFor: (ms: number) => void;
  /** How much of the cooldown is left, 0 when none. */
  heldForMs: () => number;
};

export const createPreviewQueue = (
  maxInFlight: number = MAX_PREVIEWS_IN_FLIGHT,
  now: () => number = Date.now,
  wait: (fn: () => void, ms: number) => unknown = setTimeout
): PreviewQueue => {
  let order = 0;
  let waiting: { job: PreviewJob; order: number; told?: boolean }[] = [];
  const running = new Set<string>();
  let notBefore = 0;
  let releaseArranged = false;

  const pump = () => {
    const held = notBefore - now();
    if (held > 0 && waiting.length) {
      // Nothing starts during the cooldown. One wake-up is arranged for when it ends; a later
      // holdFor that pushes it further simply arranges another when this one fires and finds
      // the queue still held.
      if (!releaseArranged) {
        releaseArranged = true;
        wait(() => {
          releaseArranged = false;
          pump();
        }, held);
      }
      waiting.forEach((w) => {
        if (w.told) return;
        w.told = true;
        w.job.onHeld?.(held);
      });
      return;
    }
    while (running.size < maxInFlight && waiting.length) {
      waiting.sort(
        (a, b) => a.job.priority - b.job.priority || a.order - b.order
      );
      const { job } = waiting.shift()!;
      if (job.stillWanted && !job.stillWanted()) continue;
      running.add(job.key);

      // Started on a microtask rather than inline, so a caller enqueuing several jobs in one loop
      // gets them ordered by priority rather than by whichever happened to arrive first.
      Promise.resolve()
        .then(job.run)
        .catch(() => undefined)
        .finally(() => {
          running.delete(job.key);
          job.onSettled?.();
          pump();
        });
    }
  };

  return {
    enqueue: (job) => {
      if (running.has(job.key) || waiting.some((w) => w.job.key === job.key)) {
        return false;
      }
      waiting.push({ job, order: order++ });
      pump();
      return true;
    },
    drop: (shouldDrop) => {
      const dropped = waiting.filter((w) => shouldDrop(w.job));
      waiting = waiting.filter((w) => !shouldDrop(w.job));
      return dropped.map((w) => w.job);
    },
    inFlight: () => running.size,
    queued: () => waiting.length,
    holdFor: (ms) => {
      notBefore = Math.max(notBefore, now() + ms);
    },
    heldForMs: () => Math.max(0, notBefore - now()),
  };
};

/**
 * 503 is the server's own cap on simultaneous renders (SYM-6213, API side). 429 is the per-user
 * limit on the two preview routes. Both mean "not now", neither means "this cannot be drawn".
 */
export const isPreviewBusy = (error: unknown): boolean => {
  const status = previewFailureStatus(error);
  return status === 503 || status === 429;
};

/** What a tile says when a drawing fails and the server gave no reason. */
export const PREVIEW_FAILED_MESSAGE =
  'This design could not be shown right now.';

/**
 * The words a tile shows for a failed drawing: the server's own reason
 * (capability off, a placement this design cannot draw, no photo), else a
 * plain sentence. Never the network layer's own wording ("Response not
 * successful: Received status code 400"), which a customer saw on QA
 * (Mark, 2 Nov 2026). The server's answer sits in `result`, at the top
 * level or one level down, like the status (restLink.js:446-450).
 */
export const previewFailureMessage = (error: unknown): string => {
  const wrapper = error as {
    result?: { message?: unknown } | null;
    networkError?: { result?: { message?: unknown } | null } | null;
  } | null;
  const reason =
    wrapper?.result?.message ?? wrapper?.networkError?.result?.message;
  return typeof reason === 'string' && reason.trim() !== ''
    ? reason
    : PREVIEW_FAILED_MESSAGE;
};

/**
 * ⚠️ The status is TWO levels down, and reading only the top level is what broke this on QA.
 *
 * apollo-link-rest throws a plain Error carrying `statusCode` (restLink.js:447-449), but
 * useMutation hands that to us wrapped in an ApolloError, whose own keys are name,
 * graphQLErrors, protocolErrors, clientErrors, networkError, message, extraInfo, cause --
 * no statusCode at all. Measured against the real client on QA, 16 Sep 2026: a 503 arrives
 * as networkError.statusCode, and the tile showed Apollo's raw "Response not successful:
 * Received status code 503" because the retry never recognised it.
 *
 * Both levels are read, because the unwrapped shape is what the unit tests and any direct
 * caller produce, and the wrapped one is what the editor actually gets.
 */
const previewFailureStatus = (error: unknown): number | undefined => {
  const wrapper = error as {
    statusCode?: number;
    networkError?: { statusCode?: number } | null;
  } | null;

  return wrapper?.statusCode ?? wrapper?.networkError?.statusCode ?? undefined;
};

/** The response carrying Retry-After, at whichever level this error keeps it. */
const previewFailureResponse = (
  error: unknown
): { headers?: { get?: (name: string) => string | null } } | undefined => {
  const wrapper = error as {
    response?: { headers?: { get?: (name: string) => string | null } };
    networkError?: {
      response?: { headers?: { get?: (name: string) => string | null } };
    } | null;
  } | null;

  return wrapper?.response ?? wrapper?.networkError?.response ?? undefined;
};

/**
 * SYM-6247. The wait the server asked for when it is too long to spin through but short enough to
 * sit out with a notice, or null when it is neither.
 */
export const longBusyWaitMs = (error: unknown): number | null => {
  const header = previewFailureResponse(error)?.headers?.get?.('Retry-After');
  const seconds =
    header === null || header === undefined ? NaN : Number(header);
  if (!Number.isFinite(seconds)) return null;

  const ms = seconds * 1000;
  return ms > MAX_RETRY_DELAY_MS && ms <= MAX_BUSY_WAIT_MS ? ms : null;
};

/**
 * SYM-6247 (Astra's eighth review). The cooldown a refusal puts on the WHOLE editor, or null.
 *
 * Only the per-user limit (429) counts: it refuses every request from this user until its window
 * resets, whatever picture they are for. A slot refusal (503) is about the two render slots being
 * taken at that instant; the next request may well find one free, so it holds nothing back.
 * Any length counts, including one too long to sit out: a tile may stop waiting, the editor may
 * not forget how long the server asked for.
 */
export const perUserCooldownMs = (error: unknown): number | null => {
  if (previewFailureStatus(error) !== 429) return null;
  const header = previewFailureResponse(error)?.headers?.get?.('Retry-After');
  const seconds =
    header === null || header === undefined ? NaN : Number(header);
  return Number.isFinite(seconds) && seconds > 0 ? seconds * 1000 : null;
};

/**
 * How long to wait before retry number `attempt` (0-based) of a busy preview, or null when the
 * server asks for longer than a tile should spin -- see longBusyWaitMs for what happens then.
 */
export const busyRetryDelayMs = (
  error: unknown,
  attempt: number
): number | null => {
  const header = previewFailureResponse(error)?.headers?.get?.('Retry-After');
  const seconds =
    header === null || header === undefined ? NaN : Number(header);

  if (Number.isFinite(seconds) && seconds >= 0) {
    return seconds * 1000 > MAX_RETRY_DELAY_MS ? null : seconds * 1000;
  }

  return FALLBACK_RETRY_DELAYS_MS[
    Math.min(attempt, FALLBACK_RETRY_DELAYS_MS.length - 1)
  ];
};

export type ScheduledPreview<T> = Omit<PreviewJob, 'run'> & {
  call: () => Promise<T>;
  onSuccess: (result: T) => void;
  /** Any failure that is not "busy", or "busy" once the retries are used up. */
  onFailure: (error: unknown) => void;
  /**
   * SYM-6247. The server asked for a wait too long to spin through; the retry is already arranged
   * for when it is over. The caller swaps the spinner for a notice that it is waiting.
   */
  onWaiting?: (waitMs: number) => void;
  /**
   * Asked before a retry is queued. A tile for an ad the customer has since left must not come back
   * to life a few seconds later and occupy a slot.
   */
  stillWanted: () => boolean;
};

/**
 * Queue a preview, and on a "busy" answer queue it again after a pause.
 *
 * ⚠️ THE PAUSE HAPPENS OUTSIDE THE QUEUE, and that is the point of doing it here rather than in a
 * retry loop inside the job. A job that slept inside its slot would hold one of the three places
 * while doing nothing, so a busy server would slowly stall every other tile behind it.
 */
export const schedulePreview = <T>(
  queue: PreviewQueue,
  preview: ScheduledPreview<T>,
  attempt = 0,
  wait: (fn: () => void, ms: number) => unknown = setTimeout
): boolean => {
  const { call, onSuccess, onFailure, onWaiting, stillWanted, ...job } =
    preview;
  // Set by a busy answer, acted on only once the slot is free -- re-queuing from inside the job
  // would be refused as a duplicate of itself.
  let retryAfterMs: number | undefined;

  return queue.enqueue({
    ...job,
    stillWanted,
    // Found waiting behind a cooldown longer than a tile should spin: the request is arranged for
    // when it lifts, so the caller shows the waiting notice now rather than a spinner (SYM-5919).
    onHeld: (heldMs) => {
      if (heldMs > MAX_RETRY_DELAY_MS) onWaiting?.(heldMs);
    },
    run: () =>
      call().then(onSuccess, (error) => {
        // Whatever happens to THIS picture below, the per-user cooldown is the editor's to keep:
        // every request behind it in the queue, and every one enqueued before it lifts, waits.
        const cooldown = perUserCooldownMs(error);
        if (cooldown !== null) queue.holdFor(cooldown);

        const delay =
          isPreviewBusy(error) && attempt < MAX_BUSY_RETRIES
            ? busyRetryDelayMs(error, attempt)
            : null;
        if (delay !== null) {
          retryAfterMs = delay;
          return;
        }
        // Too long to spin through, short enough to sit out: say so, wait the full time, retry once
        // more. It spends one of the busy retries, so a limit that never lifts still ends.
        const longWait =
          isPreviewBusy(error) && attempt < MAX_BUSY_RETRIES
            ? longBusyWaitMs(error)
            : null;
        if (longWait !== null) {
          retryAfterMs = longWait;
          onWaiting?.(longWait);
          return;
        }
        onFailure(error);
      }),
    onSettled: () => {
      if (retryAfterMs === undefined) return;
      wait(() => {
        if (stillWanted()) schedulePreview(queue, preview, attempt + 1, wait);
      }, retryAfterMs);
    },
  });
};

/**
 * SYM-6213. Whether a failed response should stay out of the red toast.
 *
 * NotificationContext turns every non-2xx into a toast. For a "busy" preview that would put a red
 * box on the screen for something the editor is already quietly retrying -- and during a rush, one
 * per tile. Deliberately narrow: only the two preview routes, only the two "not now" statuses. A
 * genuine refusal (400) from the same route still reaches the customer.
 */
export const isQuietPreviewFailure = (
  uri: RequestInfo | string,
  status: number
): boolean => {
  const url = typeof uri === 'string' ? uri : uri?.url ?? '';
  return (
    (status === 503 || status === 429) && /\/template-preview(\?|$)/.test(url)
  );
};
